import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { DocumentDebugInfoCard } from '@client/components/_shared/documents/DocumentDebugInfoCard';
import {
    DocumentSummaryDisplay,
    ExtractedTextDisplay,
    ExtractionFieldsDisplay,
} from '@client/components/_shared/documents/ExtractionFieldsDisplay';
import { ProcessFileButton } from '@client/components/_shared/documents/ProcessFileButton';
import { ProcessingStatusBadge } from '@client/components/_shared/documents/ProcessingStatusBadge';
import { SimilarDocumentsPanel } from '@client/components/_shared/documents/SimilarDocumentsPanel';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { DocumentCategoryTag } from '@client/components/_ui/display/tags';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { Space } from '@client/components/_ui/layout/Space';
import { ICONS } from '@client/constants/icons';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useIsUIDebugInfoEnabled } from '@client/hooks/system/useIsUIDebugInfoEnabled';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { trpc } from '@client/trpc/client';
import type { ShareableRouteContext } from '@client/types/router';
import { cn } from '@client/utils/styling/cn';
import { INSURANCE_POLICY_CATEGORIES } from '@shared/constants';
import type { CategorySource, DocumentCategory, ExtractionStatus } from '@shared/types';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Drawer, Spin, Tabs, Tag, Tooltip } from 'antd';
import {
    AlertCircle,
    CheckCircle,
    Combine,
    Download,
    ExternalLink,
    FileText,
    Hash,
    Info,
    Key,
    Layers,
    Sparkles,
    Tag as TagIcon,
    XCircle,
} from 'lucide-react';
import { useCallback } from 'react';
import { match } from 'ts-pattern';

export const Route = createFileRoute('/console/$orgId/documents/details/$fileId')({
    params: {
        parse({ fileId }) {
            return { fileId };
        },
    },
    staticData: {
        breadcrumb: { title: 'Document details' },
        ui: {
            icon: ICONS.documents,
        },
        presentation: 'overlay',
        featureFlag: 'enable-document-service',
    },
    async beforeLoad(ctx): Promise<ShareableRouteContext> {
        async function title() {
            const result = await ctx.context.trpcUtils.documentProcessing.getExtractionResult.ensureData({
                fileId: ctx.params.fileId,
            });
            return result?.fileName || 'Document details';
        }
        return {
            ui: { title },
            breadcrumb: { title },
        };
    },
    component: DocumentDetails,
});

function DocumentDetails() {
    const { formatDate } = useFormatter();
    const { trpcUtils } = useTrpc();
    const navigate = useNavigate();
    const orgId = useOrgId();
    const { fileId } = Route.useParams();
    const isDebugEnabled = useIsUIDebugInfoEnabled();

    const { data, isPending, error, refetch } = trpc.documentProcessing.getExtractionResult.useQuery(
        { fileId },
        {
            throwOnError(err) {
                match(err.data?.errorId)
                    .with('recordNotFound', () => {
                        void errorMessage
                            .showAsync(
                                "The document you are trying to access either doesn't exist or you don't have permissions to access it.",
                            )
                            .then(() => {
                                navigate({
                                    to: '/console/$orgId/documents',
                                    params: { orgId },
                                });
                            });
                    })
                    .otherwise(() => {
                        errorMessage.show(err);
                    });
                return false;
            },
        },
    );

    const isInsurancePolicy =
        data?.classification?.category &&
        (INSURANCE_POLICY_CATEGORIES as string[]).includes(data.classification.category);

    const { data: subtypeData } = trpc.insuranceDocuments.detectSubtype.useQuery(
        isInsurancePolicy ? { fileId } : skipToken,
    );

    const handleReprocessSuccess = () => {
        void refetch();
    };

    const handleClose = () => {
        navigate({
            to: '/console/$orgId/documents',
            params: { orgId },
        });
    };

    const handleDownload = useCallback(async () => {
        if (!data?.fileKey) return;
        const url = await trpcUtils.file.getDownloadUrl.fetch({ key: data.fileKey });
        window.open(url, '_blank');
    }, [data?.fileKey, trpcUtils]);

    const handleSimilarDocumentClick = useCallback(
        (similarFileId: string) => {
            navigate({
                to: '/console/$orgId/documents/details/$fileId',
                params: { orgId, fileId: similarFileId },
            });
        },
        [navigate, orgId],
    );

    // Check if there are any entity associations
    const hasEntityAssociations =
        data?.entityAssociations &&
        (data.entityAssociations.claimId ||
            data.entityAssociations.complaintId ||
            data.entityAssociations.incidentId ||
            data.entityAssociations.conflictOfInterestId);

    return (
        <PageController route={Route}>
            <Drawer
                open
                onClose={handleClose}
                width={720}
                classNames={{
                    header: '[&_.ant-drawer-header-title]:min-w-0 [&_.ant-drawer-header-title]:overflow-hidden [&_.ant-drawer-close]:shrink-0',
                }}
                title={
                    <Tooltip title={data?.fileName}>
                        <div className="flex min-w-0 items-center gap-2">
                            <FileText size={20} className="shrink-0 text-accent-500" />
                            <span className="truncate">{data?.fileName || 'Document details'}</span>
                        </div>
                    </Tooltip>
                }
                extra={
                    <Space size="small">
                        {data?.fileKey && (
                            <Button type="tertiary" size="small" icon={<Download />} onClick={handleDownload}>
                                Download
                            </Button>
                        )}
                        {data?.status?.extractionStatus && (
                            <ProcessFileButton
                                fileId={fileId}
                                extractionStatus={data.status.extractionStatus}
                                onSuccess={handleReprocessSuccess}
                                size="small"
                            />
                        )}
                    </Space>
                }
            >
                <TRPCErrorBoundary error={error}>
                    {isPending ? (
                        <div className="flex h-96 items-center justify-center">
                            <Spin size="large" tip="Loading document details..." />
                        </div>
                    ) : data ? (
                        <div className="space-y-6">
                            {/* File Info Header */}
                            <Card size="small">
                                <div className="space-y-3">
                                    <p className="m-0 break-all font-medium text-sm">{data.fileName}</p>
                                    <div className="flex items-center justify-between gap-4">
                                        <div className="min-w-0 flex-1">
                                            <span className="text-muted-foreground text-sm">{data.mimeType}</span>
                                        </div>
                                        <ProcessingStatusBadge
                                            status={data.status?.extractionStatus as ExtractionStatus}
                                            error={data.status?.error}
                                            invalidContentReason={data.status?.invalidContentReason}
                                            variant="filled"
                                        />
                                    </div>
                                </div>
                            </Card>

                            {/* Entity Associations */}
                            {hasEntityAssociations && (
                                <Card
                                    size="small"
                                    title={
                                        <div className="flex items-center gap-2">
                                            <ExternalLink size={16} className="text-blue-500 dark:text-blue-400" />
                                            <span>Linked entities</span>
                                        </div>
                                    }
                                >
                                    <div className="space-y-2">
                                        {data.entityAssociations.claimId && (
                                            <div className="flex items-center justify-between">
                                                <span className="text-muted-foreground text-sm">Claim</span>
                                                <Button.Link
                                                    to="/console/$orgId/claims/details/$id"
                                                    params={{
                                                        orgId,
                                                        id: data.entityAssociations.claimId,
                                                    }}
                                                    variant="link"
                                                    size="small"
                                                >
                                                    {data.entityAssociations.claimGeneratedId ??
                                                        `#${data.entityAssociations.claimId}`}
                                                </Button.Link>
                                            </div>
                                        )}
                                        {data.entityAssociations.complaintId && (
                                            <div className="flex items-center justify-between">
                                                <span className="text-muted-foreground text-sm">Complaint</span>
                                                <Button.Link
                                                    to="/console/$orgId/compliance/complaints/details/$id"
                                                    params={{
                                                        orgId,
                                                        id: data.entityAssociations.complaintId,
                                                    }}
                                                    variant="link"
                                                    size="small"
                                                >
                                                    {data.entityAssociations.complaintUuid ??
                                                        `#${data.entityAssociations.complaintId}`}
                                                </Button.Link>
                                            </div>
                                        )}
                                        {data.entityAssociations.incidentId && (
                                            <div className="flex items-center justify-between">
                                                <span className="text-muted-foreground text-sm">Incident</span>
                                                <Button.Link
                                                    to="/console/$orgId/compliance/incidents/details/$id/manage"
                                                    params={{
                                                        orgId,
                                                        id: data.entityAssociations.incidentId,
                                                    }}
                                                    variant="link"
                                                    size="small"
                                                >
                                                    {data.entityAssociations.incidentUuid ??
                                                        `#${data.entityAssociations.incidentId}`}
                                                </Button.Link>
                                            </div>
                                        )}
                                        {data.entityAssociations.conflictOfInterestId && (
                                            <div className="flex items-center justify-between">
                                                <span className="text-muted-foreground text-sm">
                                                    Conflict of interest
                                                </span>
                                                <span className="text-sm">
                                                    #{String(data.entityAssociations.conflictOfInterestId)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </Card>
                            )}

                            {/* Classification Section */}
                            <Card
                                size="small"
                                title={
                                    <div className="flex items-center gap-2">
                                        <TagIcon size={16} className="text-blue-500 dark:text-blue-400" />
                                        <span>Classification</span>
                                        {data.isMultiType && (
                                            <Tooltip title="This document contains multiple document types">
                                                <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 font-medium text-violet-700 text-xs dark:bg-violet-950/50 dark:text-violet-400">
                                                    <Combine size={12} />
                                                    Combined
                                                </span>
                                            </Tooltip>
                                        )}
                                    </div>
                                }
                            >
                                <div className="space-y-3">
                                    {/* Primary Category */}
                                    <div className="flex items-center justify-between">
                                        <span className="text-muted-foreground">
                                            {data.isMultiType ? 'Primary category' : 'Category'}
                                        </span>
                                        {data.classification?.category ? (
                                            <DocumentCategoryTag
                                                value={data.classification.category as DocumentCategory}
                                            />
                                        ) : (
                                            <span className="text-muted-foreground">Not classified</span>
                                        )}
                                    </div>

                                    {data.classification?.confidence !== null &&
                                        data.classification?.confidence !== undefined && (
                                            <div className="flex items-center justify-between">
                                                <span className="text-muted-foreground">Confidence</span>
                                                <ConfidenceIndicator confidence={data.classification.confidence} />
                                            </div>
                                        )}

                                    {data.classification?.source && (
                                        <div className="flex items-center justify-between">
                                            <span className="text-muted-foreground">Source</span>
                                            <CategorySourceBadge
                                                source={data.classification.source as CategorySource}
                                            />
                                        </div>
                                    )}

                                    {subtypeData?.detectedSubtype && (
                                        <div className="flex items-center justify-between">
                                            <span className="text-muted-foreground">Insurance subtype</span>
                                            <Tag variant="outlined" color="blue" className="!m-0">
                                                {subtypeData.subtypeDescriptions[
                                                    subtypeData.detectedSubtype as keyof typeof subtypeData.subtypeDescriptions
                                                ] ?? subtypeData.detectedSubtype}
                                            </Tag>
                                        </div>
                                    )}

                                    {/* Keywords */}
                                    {data.keywords && data.keywords.length > 0 && (
                                        <div className="mt-2 border-separator border-t pt-3">
                                            <div className="mb-2 flex items-center gap-1.5 text-muted-foreground text-sm">
                                                <Key size={14} />
                                                Keywords
                                            </div>
                                            <div className="flex flex-wrap gap-1">
                                                {data.keywords.map((kw: string) => (
                                                    <Tag key={kw} variant="outlined" className="!m-0">
                                                        {kw}
                                                    </Tag>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Secondary Categories */}
                                    {data.isMultiType &&
                                        data.secondaryCategories &&
                                        data.secondaryCategories.length > 0 && (
                                            <div className="mt-4 border-separator border-t pt-4">
                                                <div className="mb-2 font-medium text-muted-foreground text-sm">
                                                    Secondary categories
                                                </div>
                                                <div className="space-y-2">
                                                    {data.secondaryCategories.map(
                                                        (
                                                            sc: {
                                                                category: string;
                                                                confidence: number;
                                                                source: string;
                                                            },
                                                            index: number,
                                                        ) => (
                                                            <div
                                                                key={index}
                                                                className="flex items-center justify-between rounded-md bg-muted px-3 py-2"
                                                            >
                                                                <DocumentCategoryTag
                                                                    value={sc.category as DocumentCategory}
                                                                />
                                                                <ConfidenceIndicator confidence={sc.confidence} />
                                                            </div>
                                                        ),
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                </div>
                            </Card>

                            {/* Processing Status Section */}
                            <Card
                                size="small"
                                title={
                                    <div className="flex items-center gap-2">
                                        <Info size={16} className="text-subtle-foreground" />
                                        <span>Processing details</span>
                                    </div>
                                }
                            >
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-muted-foreground">Status</span>
                                        <ProcessingStatusBadge
                                            status={data.status?.extractionStatus as ExtractionStatus}
                                            error={data.status?.error}
                                            invalidContentReason={data.status?.invalidContentReason}
                                        />
                                    </div>

                                    {data.status?.extractionMethod && (
                                        <div className="flex items-center justify-between">
                                            <span className="text-muted-foreground">Extraction method</span>
                                            <span>{formatExtractionMethod(data.status.extractionMethod)}</span>
                                        </div>
                                    )}

                                    {data.status?.extractedAt && (
                                        <div className="flex items-center justify-between">
                                            <span className="text-muted-foreground">Processed at</span>
                                            <span>{formatDate(data.status.extractedAt)}</span>
                                        </div>
                                    )}

                                    <div className="flex items-center justify-between">
                                        <span className="text-muted-foreground">Has embedding</span>
                                        <EmbeddingIndicator
                                            hasEmbedding={data.hasEmbedding}
                                            hasExtractedText={!!data.extractedText}
                                        />
                                    </div>

                                    {data.status?.error && (
                                        <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/30">
                                            <div className="flex items-start gap-2">
                                                <AlertCircle size={16} className="mt-0.5 flex-shrink-0 text-red-500" />
                                                <span className="text-red-700 text-sm dark:text-red-400">
                                                    {data.status.error}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Card>

                            {/* Content Tabs */}
                            <Card size="small">
                                <Tabs
                                    defaultActiveKey="extraction"
                                    size="small"
                                    items={[
                                        {
                                            key: 'extraction',
                                            label: (
                                                <span className="flex items-center gap-2">
                                                    <Layers size={14} />
                                                    Extracted data
                                                </span>
                                            ),
                                            children: (
                                                <div className="space-y-4 pt-2">
                                                    <ExtractionFieldsDisplay
                                                        extraction={data.extraction as any}
                                                        categoryExtraction={data.categoryExtraction as any}
                                                        layout="vertical"
                                                        column={1}
                                                    />
                                                </div>
                                            ),
                                        },
                                        // Secondary extractions tab (only shown for multi-type documents)
                                        ...(data.isMultiType && data.secondaryExtractions?.length
                                            ? [
                                                  {
                                                      key: 'secondary',
                                                      label: (
                                                          <span className="flex items-center gap-2">
                                                              <Combine size={14} />
                                                              Secondary extractions
                                                          </span>
                                                      ),
                                                      children: (
                                                          <div className="space-y-6 pt-2">
                                                              {data.secondaryExtractions.map(
                                                                  (
                                                                      se: {
                                                                          category: string;
                                                                          extraction: Record<string, unknown>;
                                                                          extractedAt: string;
                                                                      },
                                                                      index: number,
                                                                  ) => (
                                                                      <SecondaryExtractionSection
                                                                          key={index}
                                                                          category={se.category as DocumentCategory}
                                                                          extraction={se.extraction}
                                                                          extractedAt={se.extractedAt}
                                                                      />
                                                                  ),
                                                              )}
                                                          </div>
                                                      ),
                                                  },
                                              ]
                                            : []),
                                        {
                                            key: 'summary',
                                            label: (
                                                <span className="flex items-center gap-2">
                                                    <Sparkles size={14} />
                                                    Summary
                                                </span>
                                            ),
                                            children: data.summary ? (
                                                <div className="pt-2">
                                                    <DocumentSummaryDisplay summary={data.summary} />
                                                </div>
                                            ) : (
                                                <div className="py-8 text-center text-subtle-foreground">
                                                    No summary available
                                                </div>
                                            ),
                                        },
                                        {
                                            key: 'text',
                                            label: (
                                                <span className="flex items-center gap-2">
                                                    <FileText size={14} />
                                                    Extracted text
                                                </span>
                                            ),
                                            children: (
                                                <div className="pt-2">
                                                    <ExtractedTextDisplay
                                                        text={data.extractedText}
                                                        maxHeight={400}
                                                        showViewToggle
                                                    />
                                                </div>
                                            ),
                                        },
                                    ]}
                                />
                            </Card>

                            {/* Similar Documents */}
                            <SimilarDocumentsPanel
                                fileId={fileId}
                                showSettings
                                onDocumentClick={handleSimilarDocumentClick}
                                onViewExtraction={handleSimilarDocumentClick}
                            />

                            {/* Debug Information Panel */}
                            {isDebugEnabled && (
                                <DocumentDebugInfoCard
                                    extractionMethod={data.status?.extractionMethod}
                                    extractedAt={data.status?.extractedAt}
                                    extractionError={data.status?.error}
                                    categorySource={data.classification?.source as CategorySource}
                                    categoryConfidence={data.classification?.confidence}
                                    rawCategory={data.classification?.category}
                                    embeddingDetails={data.embeddingDetails}
                                />
                            )}
                        </div>
                    ) : null}
                </TRPCErrorBoundary>
            </Drawer>
        </PageController>
    );
}

/**
 * Confidence level indicator with colour coding
 */
function ConfidenceIndicator({ confidence }: { confidence: number }) {
    const percent = Math.round(confidence * 100);

    const getColor = () => {
        if (percent >= 80) return 'text-green-600 dark:text-green-400';
        if (percent >= 60) return 'text-yellow-600 dark:text-yellow-400';
        return 'text-red-600 dark:text-red-400';
    };

    const getIcon = () => {
        if (percent >= 80) return <CheckCircle size={14} />;
        if (percent >= 60) return <AlertCircle size={14} />;
        return <XCircle size={14} />;
    };

    return (
        <Tooltip title={`${percent}% confident in classification`}>
            <span className={cn('inline-flex items-center gap-1.5 font-medium', getColor())}>
                {getIcon()}
                {percent}%
            </span>
        </Tooltip>
    );
}

/**
 * Badge showing category source (how the category was determined)
 */
function CategorySourceBadge({ source }: { source: CategorySource }) {
    const sourceConfig: Record<CategorySource, { label: string; color: string; icon: React.ReactNode }> = {
        AI_CLASSIFIED: {
            label: 'AI classified',
            color: 'text-purple-600 dark:text-purple-400',
            icon: <Sparkles size={14} />,
        },
        FILENAME_PATTERN: {
            label: 'Filename pattern',
            color: 'text-blue-600 dark:text-blue-400',
            icon: <FileText size={14} />,
        },
        MIME_TYPE: { label: 'MIME type', color: 'text-cyan-600 dark:text-cyan-400', icon: <Hash size={14} /> },
        USER_SPECIFIED: {
            label: 'User specified',
            color: 'text-green-600 dark:text-green-400',
            icon: <CheckCircle size={14} />,
        },
        UPLOAD_CONTEXT: {
            label: 'Upload context',
            color: 'text-orange-600 dark:text-orange-400',
            icon: <Info size={14} />,
        },
    };

    const config = sourceConfig[source];

    return (
        <span className={cn('inline-flex items-center gap-1.5 text-sm', config.color)}>
            {config.icon}
            {config.label}
        </span>
    );
}

/**
 * Embedding status indicator
 */
function EmbeddingIndicator({ hasEmbedding, hasExtractedText }: { hasEmbedding: boolean; hasExtractedText: boolean }) {
    if (hasEmbedding) {
        return (
            <Tooltip title="Document has vector embeddings for semantic search">
                <span className="inline-flex items-center gap-1.5 text-green-600 dark:text-green-400">
                    <CheckCircle size={14} />
                    Indexed
                </span>
            </Tooltip>
        );
    }

    const reason = hasExtractedText
        ? 'Embeddings have not been generated yet. Try reprocessing the document.'
        : 'No text content available. Embeddings require extracted text.';

    return (
        <Tooltip title={reason}>
            <span className="inline-flex items-center gap-1.5 text-subtle-foreground">
                <XCircle size={14} />
                Not indexed
            </span>
        </Tooltip>
    );
}

/**
 * Format extraction method for display
 */
function formatExtractionMethod(method: string): string {
    const methods: Record<string, string> = {
        DATALAB: 'Datalab Marker',
        MISTRAL_OCR: 'Mistral OCR',
        LLM_VISION: 'LLM Vision',
        TEXT: 'Text extraction',
    };

    return methods[method] || method;
}

/**
 * Section displaying secondary extraction data for multi-type documents
 */
function SecondaryExtractionSection({
    category,
    extraction,
    extractedAt,
}: {
    category: DocumentCategory;
    extraction: Record<string, unknown>;
    extractedAt: string;
}) {
    const { formatDate } = useFormatter();

    return (
        <div className="rounded-lg border border-separator">
            <div className="flex items-center justify-between border-separator border-b bg-muted px-4 py-3">
                <div className="flex items-center gap-2">
                    <DocumentCategoryTag value={category} />
                </div>
                {extractedAt && (
                    <span className="text-muted-foreground text-xs">Extracted {formatDate(new Date(extractedAt))}</span>
                )}
            </div>
            <div className="p-4">
                <ExtractionFieldsDisplay
                    extraction={extraction as any}
                    categoryExtraction={null}
                    layout="vertical"
                    column={1}
                />
            </div>
        </div>
    );
}
